/**
 * LayoutPresets - Selector de configuraciones/vistas predefinidas
 * Con opción de crear configuraciones personalizadas
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';
import { createPortal } from 'react-dom';
import { 
  SquaresFour, ChartLine, Lightning, Briefcase, MagnifyingGlass,
  FloppyDisk, Trash, Check, X, CaretDown, Plus, Gear, Info,
  ChartBar, TrendUp, Scales, ChartPieSlice, Database, Buildings,
  Newspaper, Bell, Eye, Calculator, GridFour, ArrowsClockwise, Target, Globe, Lock, Star
} from '@phosphor-icons/react';
import { WINDOWS_CONFIG } from './WindowManager';

// Descripciones detalladas de cada herramienta
const TOOL_DESCRIPTIONS = {
  'analyzer': {
    name: 'Analizador de Activos',
    shortDesc: 'Análisis completo de cualquier activo',
    fullDesc: 'Obtén información detallada de cualquier acción, ETF o criptomoneda. Incluye precio actual, rango de 52 semanas, volatilidad, retornos históricos y sentimiento del mercado.',
    features: ['Búsqueda de cualquier ticker', 'Gráfico de precios', 'Guardar en historial', 'Exportar a PDF']
  },
  'candlestickChart': {
    name: 'Mapa Velas Ampliado',
    shortDesc: 'Gráfico profesional ampliable',
    fullDesc: 'Visualiza los movimientos de precio con gráficos de velas japonesas. Ideal para ver patrones de precio y tomar decisiones de compra/venta.',
    features: ['Zoom y desplazamiento', 'Múltiples períodos', 'Pantalla completa', 'Cambiar activo']
  },
  'heatmap': {
    name: 'Análisis de Correlación',
    shortDesc: 'Relación entre tus activos',
    fullDesc: 'Descubre cómo se relacionan tus inversiones entre sí. Te ayuda a diversificar mejor tu cartera identificando qué activos se mueven juntos o en direcciones opuestas.',
    features: ['Sin límite de activos', 'Filtros avanzados', 'Estadísticas de diversificación', 'Períodos ajustables']
  },
  'extremes': {
    name: 'Historial Máx/Mín',
    shortDesc: 'Máximos y mínimos históricos',
    fullDesc: 'Consulta los precios máximos y mínimos históricos de cualquier activo. Útil para saber si un activo está cerca de su máximo histórico o en zona de oportunidad.',
    features: ['Máximo/Mínimo histórico (ATH/ATL)', 'Máx/Mín 52 semanas', 'Gráfico con marcadores', 'Distancia al ATH']
  },
  'indicadoresEnhanced': {
    name: 'Análisis Técnico Profesional',
    shortDesc: 'Indicadores para saber cuándo comprar/vender',
    fullDesc: 'Análisis simplificado con recomendaciones claras: COMPRAR, VENDER o MANTENER. Basado en indicadores técnicos reales (RSI, MACD) explicados en lenguaje sencillo.',
    features: ['Recomendación clara', 'Buscar cualquier activo', 'Explicaciones simples', 'Tips para principiantes']
  },
  'indicadoresPro': {
    name: 'Análisis Técnico Profesional',
    shortDesc: 'Indicadores + Conclusión IA y Proyección',
    fullDesc: 'El panel más completo de análisis técnico. RSI, MACD, Bollinger, ADX, Fibonacci y ATR con explicaciones, más Conclusión Profesional con IA y proyección alcista/bajista a corto, medio y largo plazo.',
    features: ['RSI, MACD, Bollinger', 'ADX, Fibonacci, ATR', 'Conclusión Profesional IA', 'Proyección corto/medio/largo']
  },
  'rendimiento': {
    name: 'Métricas de Rendimiento',
    shortDesc: 'Estadísticas de performance',
    fullDesc: 'Analiza el rendimiento histórico de cualquier activo con métricas profesionales como Sharpe Ratio, volatilidad y drawdown máximo.',
    features: ['Sharpe Ratio', 'Volatilidad', 'Drawdown', 'Retornos anualizados']
  },
  'signals': {
    name: 'Análisis Señales Trading',
    shortDesc: 'Señales automáticas de compra/venta',
    fullDesc: 'Recibe señales de trading basadas en análisis técnico automático. Detecta cruces de medias móviles, sobrecompra/sobreventa y más.',
    features: ['Señales en tiempo real', 'Múltiples indicadores', 'Historial de señales', 'Configuración personalizada']
  },
  'comparator': {
    name: 'Comparador Índices y Cripto',
    shortDesc: 'Compara índices y criptomonedas',
    fullDesc: 'Compara índices mundiales y criptomonedas. Ideal para ver el rendimiento relativo de diferentes mercados.',
    features: ['Índices globales', 'Criptomonedas', 'Gráfico comparativo', 'Info para principiantes']
  },
  'livePortfolio': {
    name: 'Portafolio en Vivo',
    shortDesc: 'Seguimiento en tiempo real',
    fullDesc: 'Monitorea tu cartera en tiempo real con actualizaciones automáticas de precios y ganancias/pérdidas.',
    features: ['Precios en vivo', 'P&L actualizado', 'Alertas de cambio', 'Vista compacta']
  },
  'backtesting': {
    name: 'Backtesting',
    shortDesc: 'Simula estrategias pasadas',
    fullDesc: 'Prueba cómo habría funcionado una estrategia de inversión en el pasado. Útil para validar ideas antes de invertir dinero real.',
    features: ['Simulación histórica', 'Múltiples estrategias', 'Resultados detallados', 'Comparativa']
  },
  'sectors': {
    name: 'Análisis de Sectores',
    shortDesc: 'Rendimiento por sector',
    fullDesc: 'Compara el rendimiento de diferentes sectores del mercado (tecnología, finanzas, salud, etc.). Te ayuda a identificar qué sectores están fuertes o débiles.',
    features: ['Todos los sectores', 'Rendimientos por período', 'Tendencias', 'Top y peores']
  },
  'portfolio': {
    name: 'Resumen de Cartera',
    shortDesc: 'Tu cartera en detalle',
    fullDesc: 'Vista completa de todas tus inversiones con ganancias/pérdidas, distribución por sectores y evolución temporal.',
    features: ['Ganancias totales', 'Distribución', 'Historial', 'Exportar']
  },
  'news': {
    name: 'Noticias del Mercado',
    shortDesc: 'Noticias que afectan tus inversiones',
    fullDesc: 'Las últimas noticias relevantes para tus activos. Incluye noticias de alto impacto que pueden mover el mercado.',
    features: ['Noticias en tiempo real', 'Filtro por cartera', 'Alto impacto', 'Traducción automática']
  },
  'alerts': {
    name: 'Alertas de Precio',
    shortDesc: 'Notificaciones de precio',
    fullDesc: 'Configura alertas para recibir notificaciones cuando un activo alcance un precio específico. No te pierdas oportunidades.',
    features: ['Alertas personalizadas', 'Múltiples activos', 'Historial de alertas', 'Notificaciones']
  },
  'indices': {
    name: 'Comparar con Índices',
    shortDesc: '¿Le ganas al mercado?',
    fullDesc: 'Compara el rendimiento de tu cartera o activo con los principales índices (S&P 500, IBEX 35, DAX). Descubre si estás superando al mercado.',
    features: ['S&P 500, NASDAQ, IBEX...', 'Gráfico comparativo', 'Múltiples períodos', 'Añadir índices']
  },
  'categories': {
    name: 'Análisis de Sectores',
    shortDesc: 'Descubre nuevas oportunidades',
    fullDesc: 'Navega por diferentes categorías y sectores del mercado para descubrir nuevos activos interesantes para invertir.',
    features: ['Por sector', 'Por región', 'Por tipo de activo', 'Búsqueda avanzada']
  },
  'calculators': {
    name: 'Calculadoras',
    shortDesc: 'Herramientas de cálculo',
    fullDesc: 'Calculadoras útiles para inversores: riesgo, posición, rentabilidad esperada y más.',
    features: ['Tamaño de posición', 'Riesgo/beneficio', 'Rentabilidad', 'Stop loss']
  },
  'fullscreen': {
    name: 'Cartera Detallada',
    shortDesc: 'Modo presentación',
    fullDesc: 'Vista optimizada para pantallas grandes o presentaciones. Muestra la información más importante de forma clara.',
    features: ['Datos en grande', 'Modo oscuro', 'Auto-actualización', 'Sin distracciones']
  },
  'dataArchive': {
    name: 'Archivo de Datos',
    shortDesc: 'Historial y evolución',
    fullDesc: 'Guarda snapshots diarios de tu cartera y visualiza su evolución a lo largo del tiempo con gráficos 3D.',
    features: ['Snapshots diarios', 'Gráficos de evolución', 'Exportar CSV/PDF', 'Filtros avanzados']
  },
  'buystrategy': {
    name: 'Estrategia de Compra',
    shortDesc: 'DCA, rebalanceo y objetivos',
    fullDesc: 'Planifica tus compras con estrategias DCA, define objetivos de precio y calcula rebalanceos óptimos.',
    features: ['Calculadora DCA', 'Objetivos de precio', 'Rebalanceo', 'Valoración de activos']
  },
  'notifications': {
    name: 'Notificaciones',
    shortDesc: 'Push y Email',
    fullDesc: 'Configura notificaciones push en tu navegador y alertas por email para no perderte ningún movimiento importante.',
    features: ['Push notifications', 'Alertas email', 'Configuración personalizada', 'Historial']
  },
  'finvizMap': {
    name: 'Mapa de Mercado Finviz',
    shortDesc: 'Treemap visual del mercado',
    fullDesc: 'Visualiza todo el mercado en un mapa de calor tipo Finviz. Ve de un vistazo qué sectores y acciones están subiendo o bajando. Perfecto para entender el estado general del mercado.',
    features: ['Mapa por sectores', 'S&P 500, Europa, IBEX', 'Filtros por sector', 'Click para detalles']
  },
  'contabilidad': {
    name: 'Contabilidad',
    shortDesc: 'Registro de beneficios diarios',
    fullDesc: 'Guarda automáticamente el estado de tu cartera cada día. Visualiza lo que llevas ganado esta semana, mes y año con gráficos claros. Incluye historial detallado por días, semanas, meses y años.',
    features: ['Auto-guardado diario', 'Resumen semana/mes/año', 'Historial detallado', 'Gráfico de barras'],
    requiredPlan: 'ultimate'
  },
  'multiChart': {
    name: 'Multi-Gráfico (4 Activos)',
    shortDesc: 'Analiza 4 activos simultáneamente',
    fullDesc: 'Visualiza y compara hasta 4 activos diferentes al mismo tiempo, cada uno con su propio gráfico y controles independientes. Perfecto para comparar o monitorear varios activos a la vez.',
    features: ['4 gráficos independientes', 'Selecciona cualquier activo', 'Períodos ajustables', 'Vista en pantalla completa']
  },
  'fundamentalAnalysis': {
    name: 'Análisis con IA',
    shortDesc: 'Análisis profesional con Inteligencia Artificial',
    fullDesc: 'Obtén un análisis fundamental completo generado por IA. Incluye score de inversión, métricas clave, fortalezas, riesgos y recomendación automática.',
    features: ['Score 0-100', 'Análisis con IA', 'Métricas fundamentales', 'Recomendación automática'],
    requiredPlan: 'ultimate'
  },
  'ebitdaAnalysis': {
    name: 'Análisis Margen EBITDA',
    shortDesc: 'Métricas fundamentales con énfasis en EBITDA',
    fullDesc: 'Analiza el margen EBITDA y otras métricas fundamentales de cualquier empresa o de tu cartera completa. Incluye ROE, ROA, Deuda/EBITDA, P/E, EV/EBITDA y evolución trimestral.',
    features: ['Margen EBITDA', 'ROE/ROA', 'Deuda/EBITDA', 'Historial trimestral', 'Cartera completa'],
    requiredPlan: 'ultimate'
  },
  'comparator': {
    name: 'Comparador Índices y Cripto',
    shortDesc: 'Compara índices mundiales y criptomonedas',
    fullDesc: 'Compara el rendimiento de los principales índices bursátiles (S&P 500, IBEX 35, DAX, Nikkei) con criptomonedas (Bitcoin, Ethereum). Ideal para diversificar y entender correlaciones globales.',
    features: ['10 índices mundiales', '12 criptomonedas', 'Gráfico comparativo', 'Datos en tiempo real']
  },
  'financialDatabase': {
    name: 'Datos Financieros',
    shortDesc: 'Fundamentales y ratios de cualquier empresa',
    fullDesc: 'Accede a datos financieros completos: P/E, dividendos, capitalización, ingresos, beneficios y más. Incluye recomendaciones de analistas y precio objetivo.',
    features: ['Ratios fundamentales', 'Recomendaciones analistas', 'Precio objetivo', 'Histórico de dividendos']
  },
  'marketDatabase': {
    name: 'Base de Datos',
    shortDesc: 'Tabla con todos los activos del mercado',
    fullDesc: 'Explora una base de datos completa con miles de acciones, ETFs y criptos. Ordena y filtra por cualquier métrica: precio, P/E, dividendo, capitalización.',
    features: ['Miles de activos', 'Ordenar por columnas', 'Filtros avanzados', 'Exportar datos']
  },
  'historicalData': {
    name: 'Datos Históricos',
    shortDesc: 'Historial de cotizaciones tipo Excel',
    fullDesc: 'Consulta el historial completo de cualquier activo: fecha, apertura, máximo, mínimo, cierre, volumen. Exporta a CSV, PDF o imprime directamente.',
    features: ['Historial OHLCV', 'Selector de fechas', 'Exportar CSV/PDF', 'Imprimir', 'Diario/Semanal/Mensual']
  },
  'stockScreener': {
    name: 'Buscador Acciones',
    shortDesc: 'Filtra acciones por criterios',
    fullDesc: 'Encuentra acciones que cumplan tus criterios de inversión. Filtra por precio, P/E, dividendo, sector, región y más. El screener definitivo para encontrar oportunidades.',
    features: ['Filtros múltiples', 'Guardar búsquedas', 'Exportar resultados', 'Alertas de nuevos matches']
  },
  'stockComparator': {
    name: 'Comparador Pro',
    shortDesc: 'Compara acciones en detalle',
    fullDesc: 'Compara hasta 4 acciones lado a lado con todos sus datos fundamentales, técnicos y recomendaciones de analistas. Decide con información completa.',
    features: ['Hasta 4 acciones', 'Datos fundamentales', 'Gráfico histórico', 'Recomendaciones']
  },
  'backup': {
    name: 'Backup',
    shortDesc: 'Copias de seguridad de tus datos',
    fullDesc: 'Guarda y restaura todos tus datos: cartera, alertas, configuración y historial. Nunca pierdas tu información.',
    features: ['Guardar backup', 'Restaurar datos', 'Exportar JSON', 'Importar configuración']
  },
  'manual': {
    name: 'Manual',
    shortDesc: 'Guía completa de la aplicación',
    fullDesc: 'Documentación completa de KAPITAL: guía de inicio rápido, explicación de cada herramienta, glosario de términos y preguntas frecuentes.',
    features: ['Guía de inicio', 'Tutoriales', 'Glosario', 'FAQ']
  },
  'tradingJournal': {
    name: 'Trading Journal',
    shortDesc: 'Diario de operaciones',
    fullDesc: 'Registra todas tus operaciones de compra y venta. Analiza tu rendimiento, errores y aciertos para mejorar como inversor.',
    features: ['Registro de operaciones', 'Estadísticas', 'Notas personales', 'Análisis de errores']
  },
  'investmentSimulator': {
    name: 'Simulador de Inversiones',
    shortDesc: 'Simula el poder del interés compuesto',
    fullDesc: 'Descubre cómo crecerá tu dinero a largo plazo. Simula diferentes escenarios de inversión con aportaciones mensuales y diferentes tasas de rentabilidad. Incluye ajuste por inflación y cálculo de ingresos pasivos.',
    features: ['Interés compuesto', 'Presets: Conservador, Agresivo, Jubilación', 'Gráfico de evolución', 'Ingresos pasivos', 'Valor real (ajustado por inflación)'],
    requiredPlan: 'ultimate'
  },
  'financialGoals': {
    name: 'Objetivos Financieros',
    shortDesc: 'Define y alcanza tus metas de ahorro',
    fullDesc: 'Crea y gestiona múltiples objetivos de ahorro: casa, vacaciones, fondo de emergencia, jubilación. Visualiza tu progreso con gráficos y mantén el foco en tus metas financieras.',
    features: ['Múltiples objetivos', 'Tipos: Casa, Vacaciones, Jubilación, Emergencias', 'Gráficos de progreso', 'Añade aportaciones rápidas', 'Imprime tu plan'],
    requiredPlan: 'ultimate'
  }
};

// Layouts del sistema en español - TODAS las herramientas
const SYSTEM_LAYOUTS = [
  {
    id: 'trading',
    name: 'Operativa',
    description: 'Análisis técnico y señales de trading',
    icon: Lightning,
    color: '#ff9500',
    windows: ['indicadoresPro', 'candlestickChart', 'signals'],
    positions: {
      indicadoresPro: { x: 180, y: 50, width: 700, height: 700 },
      candlestickChart: { x: 950, y: 50, width: 650, height: 550 },
      signals: { x: 200, y: 80, width: 600, height: 500 }
    }
  },
  {
    id: 'analysis',
    name: 'Análisis',
    description: 'Correlaciones, sectores y comparativas',
    icon: ChartLine,
    color: '#a855f7',
    windows: ['heatmap', 'sectors', 'comparator', 'extremes', 'finvizMap'],
    positions: {
      heatmap: { x: 180, y: 50, width: 800, height: 550 },
      sectors: { x: 1000, y: 50, width: 700, height: 550 },
      comparator: { x: 200, y: 80, width: 750, height: 550 },
      extremes: { x: 220, y: 100, width: 700, height: 550 },
      finvizMap: { x: 240, y: 120, width: 1100, height: 750 }
    }
  },
  {
    id: 'portfolio',
    name: 'Gestión de Cartera',
    description: 'Gestión de portafolio, contabilidad y análisis',
    icon: Briefcase,
    color: '#00ff6a',
    windows: ['portfolio', 'livePortfolio', 'contabilidad', 'ebitdaAnalysis', 'fundamentalAnalysis', 'alerts', 'news', 'fullscreen', 'dataArchive', 'notifications'],
    positions: {
      portfolio: { x: 180, y: 50, width: 800, height: 600 },
      livePortfolio: { x: 200, y: 80, width: 850, height: 600 },
      contabilidad: { x: 220, y: 100, width: 800, height: 700 },
      ebitdaAnalysis: { x: 240, y: 120, width: 1100, height: 800 },
      fundamentalAnalysis: { x: 260, y: 140, width: 1200, height: 850 },
      alerts: { x: 1000, y: 50, width: 600, height: 500 },
      news: { x: 1000, y: 50, width: 700, height: 600 },
      fullscreen: { x: 180, y: 50, width: 1200, height: 700 },
      dataArchive: { x: 200, y: 50, width: 1200, height: 800 },
      notifications: { x: 1000, y: 50, width: 500, height: 400 }
    }
  },
  {
    id: 'research',
    name: 'Investigación',
    description: 'Explorar activos e índices',
    icon: MagnifyingGlass,
    color: '#00dcff',
    windows: ['analyzer', 'comparator', 'indices', 'categories', 'rendimiento', 'asset360', 'multiChart', 'indicadoresEnhanced'],
    positions: {
      analyzer: { x: 180, y: 50, width: 850, height: 600 },
      comparator: { x: 200, y: 80, width: 900, height: 650 },
      indices: { x: 1050, y: 50, width: 700, height: 600 },
      categories: { x: 200, y: 80, width: 800, height: 600 },
      rendimiento: { x: 220, y: 100, width: 700, height: 550 },
      asset360: { x: 240, y: 120, width: 900, height: 820 },
      multiChart: { x: 100, y: 50, width: 1400, height: 800 },
      indicadoresEnhanced: { x: 180, y: 50, width: 750, height: 600 }
    }
  },
  {
    id: 'strategy',
    name: 'Estrategia de Compra',
    description: 'Planificación y análisis para decisiones de compra',
    icon: Target,
    color: '#10b981',
    windows: ['buystrategy', 'indicadoresEnhanced', 'signals', 'alerts'],
    positions: {
      buystrategy: { x: 180, y: 50, width: 800, height: 700 },
      indicadoresEnhanced: { x: 1000, y: 50, width: 700, height: 600 },
      signals: { x: 200, y: 80, width: 650, height: 550 },
      alerts: { x: 900, y: 80, width: 600, height: 500 }
    }
  },
  {
    id: 'tools',
    name: 'Herramientas',
    description: 'Calculadoras, backtesting y utilidades',
    icon: Calculator,
    color: '#f59e0b',
    windows: ['calculators', 'backtesting', 'backup', 'manual', 'notifications'],
    positions: {
      calculators: { x: 180, y: 50, width: 700, height: 550 },
      backtesting: { x: 900, y: 50, width: 750, height: 600 },
      backup: { x: 200, y: 80, width: 600, height: 500 },
      manual: { x: 220, y: 100, width: 900, height: 700 },
      notifications: { x: 900, y: 80, width: 500, height: 400 }
    }
  },
  {
    id: 'data',
    name: 'Datos',
    description: 'Base de datos y screeners',
    icon: Database,
    color: '#8b5cf6',
    windows: ['financialDatabase', 'marketDatabase', 'stockScreener', 'stockComparator'],
    positions: {
      financialDatabase: { x: 180, y: 50, width: 900, height: 650 },
      marketDatabase: { x: 200, y: 80, width: 1000, height: 700 },
      stockScreener: { x: 220, y: 100, width: 950, height: 700 },
      stockComparator: { x: 240, y: 120, width: 1000, height: 700 }
    }
  }
];

const LayoutPresets = ({ 
  onApplyLayout,
  onOpenAlertsPanel, // Nuevo: abrir el Centro de Alertas unificado
  onOpenWindow,      // Nuevo: abrir una ventana individual SIN cerrar las demás
  currentWindows = [],
  className = '',
  currentUser = null  // Para restricciones por plan
}) => {
  // Verificar si el usuario tiene plan de pago (acepta nombres nuevos y viejos)
  const isPaidPlan = currentUser?.plan === 'pro' || currentUser?.plan === 'ultimate' || 
                     currentUser?.plan === 'basic' || currentUser?.plan === 'premium';
  
  const [isOpen, setIsOpen] = useState(false);
  const [customLayouts, setCustomLayouts] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [selectedWindows, setSelectedWindows] = useState([]);
  const [activeLayout, setActiveLayout] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const autoCloseTimerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  // Función para resetear el timer de auto-cierre (30 segundos)
  const resetAutoCloseTimer = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }
    autoCloseTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      setShowCreateDialog(false);
      setNewLayoutName('');
      setSelectedWindows([]);
      setExpandedCategory(null);
    }, 30000); // 30 segundos
  }, []);

  // Cerrar menú al hacer click fuera O después de 30 segundos de inactividad
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(e.target) && 
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowCreateDialog(false);
        setNewLayoutName('');
        setSelectedWindows([]);
        setExpandedCategory(null);
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
        }
      }
    };
    
    // Detectar actividad dentro del menú para resetear el timer
    const handleMenuActivity = () => {
      if (isOpen) {
        resetAutoCloseTimer();
      }
    };
    
    if (isOpen) {
      // Iniciar timer de 30 segundos
      resetAutoCloseTimer();
      
      // Usar setTimeout para evitar que el click que abre el menú también lo cierre
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      // Detectar movimiento del mouse/touch dentro del menú
      if (menuRef.current) {
        menuRef.current.addEventListener('mousemove', handleMenuActivity);
        menuRef.current.addEventListener('click', handleMenuActivity);
        menuRef.current.addEventListener('scroll', handleMenuActivity);
        menuRef.current.addEventListener('touchmove', handleMenuActivity);
        menuRef.current.addEventListener('touchstart', handleMenuActivity);
      }
      
      // En móvil, prevenir scroll del body cuando el menú está abierto
      if (window.innerWidth < 768) {
        document.body.style.overflow = 'hidden';
      }
    }
    
    // Guardar referencia para el cleanup
    const currentMenuRef = menuRef.current;
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (currentMenuRef) {
        currentMenuRef.removeEventListener('mousemove', handleMenuActivity);
        currentMenuRef.removeEventListener('click', handleMenuActivity);
        currentMenuRef.removeEventListener('scroll', handleMenuActivity);
        currentMenuRef.removeEventListener('touchmove', handleMenuActivity);
        currentMenuRef.removeEventListener('touchstart', handleMenuActivity);
      }
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
      // Restaurar scroll del body
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resetAutoCloseTimer]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('customLayouts');
      if (saved) setCustomLayouts(JSON.parse(saved));
    } catch (e) {
      logger.warn('Error loading custom layouts:', e);
    }
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      setDropdownPosition({
        // En móvil, posicionar desde top fijo para maximizar espacio de scroll
        top: isMobile ? 60 : rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen]);

  const saveCustomLayouts = (layouts) => {
    setCustomLayouts(layouts);
    localStorage.setItem('customLayouts', JSON.stringify(layouts));
  };

  const handleApplyLayout = (layout) => {
    if (onApplyLayout) {
      onApplyLayout(layout);
      setActiveLayout(layout.id);
    }
    setIsOpen(false);
  };

  // Crear nueva configuración con ventanas seleccionadas
  const handleCreateLayout = () => {
    if (!newLayoutName.trim() || selectedWindows.length === 0) return;
    
    // Calcular posiciones en cascada
    const positions = {};
    selectedWindows.forEach((windowId, index) => {
      positions[windowId] = {
        x: 180 + (index * 50),
        y: 50 + (index * 40),
        width: 700,
        height: 500
      };
    });

    const newLayout = {
      id: `custom_${Date.now()}`,
      name: newLayoutName.trim(),
      description: `${selectedWindows.length} ventanas`,
      icon: Gear,
      color: '#6366f1',
      isCustom: true,
      windows: selectedWindows,
      positions
    };

    saveCustomLayouts([...customLayouts, newLayout]);
    setNewLayoutName('');
    setSelectedWindows([]);
    setShowCreateDialog(false);
  };

  const handleDeleteCustomLayout = (layoutId, e) => {
    e.stopPropagation();
    saveCustomLayouts(customLayouts.filter(l => l.id !== layoutId));
  };

  const toggleWindowSelection = (windowId) => {
    setSelectedWindows(prev => 
      prev.includes(windowId) 
        ? prev.filter(id => id !== windowId)
        : [...prev, windowId]
    );
  };

  const allLayouts = [...SYSTEM_LAYOUTS, ...customLayouts];

  // Ventanas disponibles para seleccionar (del WindowManager)
  const availableWindows = WINDOWS_CONFIG.filter(w => w.id !== 'fullscreen');

  // Toggle category expansion
  const toggleCategory = (categoryId, e) => {
    e.stopPropagation();
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  // Open a single tool window
  const handleOpenSingleTool = (toolId, e) => {
    e.stopPropagation();
    
    // Si es alertas, abrir el nuevo Centro de Alertas unificado
    if (toolId === 'alerts') {
      if (onOpenAlertsPanel) {
        onOpenAlertsPanel();
      }
      setIsOpen(false);
      setExpandedCategory(null);
      return;
    }
    
    // IMPORTANTE: Usar onOpenWindow para abrir UNA herramienta sin cerrar las demás
    if (onOpenWindow) {
      logger.log(`LayoutPresets: Opening single tool via onOpenWindow: ${toolId}`);
      onOpenWindow(toolId);
    } else if (onApplyLayout) {
      // Fallback al comportamiento anterior (cierra todas las ventanas)
      logger.warn(`LayoutPresets: Using fallback onApplyLayout for ${toolId} - this will close other windows`);
      const singleToolLayout = {
        id: `single_${toolId}`,
        name: TOOL_DESCRIPTIONS[toolId]?.name || toolId,
        windows: [toolId],
        positions: {
          [toolId]: { x: 200, y: 80, width: 800, height: 550 }
        }
      };
      onApplyLayout(singleToolLayout);
    }
    setIsOpen(false);
    setExpandedCategory(null);
  };

  // Get tool info from WINDOWS_CONFIG
  const getToolFromConfig = (toolId) => {
    return WINDOWS_CONFIG.find(w => w.id === toolId);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Botón principal */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
        style={{
          background: isOpen ? 'rgba(0, 220, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
          border: `1px solid ${isOpen ? 'rgba(0, 220, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
        }}
        data-testid="layout-presets-button"
      >
        <SquaresFour size={16} className={isOpen ? 'text-gold-400' : 'text-gray-400'} />
        <span className={isOpen ? 'text-gold-300' : 'text-gray-300'}>Grupos de Herramientas</span>
        <CaretDown 
          size={12} 
          className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Mega-menu usando Portal */}
      {isOpen && createPortal(
        <div 
          ref={menuRef}
          className="fixed rounded-2xl overflow-hidden"
          style={{
              top: dropdownPosition.top,
              right: 20,
              left: 20,
              maxWidth: '1400px',
              margin: '0 auto',
              zIndex: 99999,
              background: 'linear-gradient(165deg, rgba(12, 25, 48, 0.98) 0%, rgba(8, 16, 32, 1) 50%, rgba(5, 12, 25, 1) 100%)',
              border: '1px solid rgba(0, 220, 255, 0.35)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9), 0 0 40px rgba(0, 220, 255, 0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
              backdropFilter: 'blur(20px)',
              maxHeight: 'calc(100vh - 100px)',
              display: 'flex',
              flexDirection: 'column',
            }}
            data-testid="layout-presets-dropdown"
          >
            {/* Efecto 3D superior con brillo */}
            <div 
              className="h-[3px]"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, #00ffff 20%, #00dcff 50%, #00ffff 80%, transparent 100%)',
                boxShadow: '0 0 20px rgba(0, 255, 255, 0.6), 0 0 40px rgba(0, 220, 255, 0.3)'
              }}
            />
            
            {/* Header */}
            <div 
              className="px-6 py-4 flex items-center justify-between"
              style={{ 
                borderBottom: '1px solid rgba(0, 220, 255, 0.2)',
                background: 'linear-gradient(180deg, rgba(0, 220, 255, 0.08) 0%, transparent 100%)'
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 220, 255, 0.3) 0%, rgba(0, 150, 255, 0.1) 100%)',
                    border: '1px solid rgba(0, 220, 255, 0.4)',
                    boxShadow: '0 4px 15px rgba(0, 220, 255, 0.3)'
                  }}
                >
                  <SquaresFour size={22} className="text-gold-400" weight="fill" />
                </div>
                <div>
                  <span 
                    className="text-lg font-bold"
                    style={{
                      background: 'linear-gradient(180deg, #ffffff 0%, #00dcff 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Grupos de Herramientas
                  </span>
                  <p className="text-xs text-gray-500">Haz clic en cualquier herramienta para abrirla</p>
                </div>
              </div>
              
              <button
                onClick={() => setShowCreateDialog(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 220, 255, 0.2) 0%, rgba(0, 150, 255, 0.1) 100%)',
                  border: '1px solid rgba(0, 220, 255, 0.3)',
                  color: '#00dcff'
                }}
              >
                <Plus size={16} />
                Crear Vista Personalizada
              </button>
            </div>

            {showCreateDialog ? (
              /* Panel de crear nueva configuración */
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="mb-4">
                  <label className="text-xs text-gray-400 mb-2 block">Nombre de la vista</label>
                  <input
                    type="text"
                    value={newLayoutName}
                    onChange={(e) => setNewLayoutName(e.target.value)}
                    placeholder="Mi configuración..."
                    className="w-full px-3 py-2 rounded-lg text-sm bg-black/30 border border-gold-500/30 text-white placeholder-gray-600 focus:outline-none focus:border-gold-400"
                    autoFocus
                  />
                </div>
                
                <div className="mb-4">
                  <label className="text-xs text-gray-400 mb-2 block">
                    Selecciona las herramientas ({selectedWindows.length} seleccionadas)
                  </label>
                  <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1">
                    {availableWindows.map((window) => {
                      const isSelected = selectedWindows.includes(window.id);
                      const IconComponent = window.icon;
                      const toolInfo = TOOL_DESCRIPTIONS[window.id] || {};
                      
                      return (
                        <button
                          key={window.id}
                          onClick={() => toggleWindowSelection(window.id)}
                          className={`p-3 rounded-xl transition-all text-left ${
                            isSelected ? 'bg-gold-500/20 border-gold-500/40' : 'bg-white/5 border-transparent hover:bg-white/10'
                          } border`}
                        >
                          <div className="flex items-center gap-2">
                            <IconComponent size={18} style={{ color: window.color }} />
                            <span className="text-sm font-medium text-white truncate">{toolInfo.name || window.name}</span>
                            {isSelected && <Check size={14} className="text-gold-400 ml-auto" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowCreateDialog(false);
                      setNewLayoutName('');
                      setSelectedWindows([]);
                    }}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-white/5 text-gray-400 hover:bg-white/10 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreateLayout}
                    disabled={!newLayoutName.trim() || selectedWindows.length === 0}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-zinc-900/60 text-zinc-100 border border-gold-500/40 hover:bg-zinc-800/70 hover:border-gold-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Crear Vista
                  </button>
                </div>
              </div>
            ) : (
              /* MEGA-MENU: Grid de categorías con herramientas */
              <div className="p-4 overflow-y-auto flex-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                {/* Mensaje de bloqueo para plan gratis */}
                {!isPaidPlan && (
                  <div 
                    className="mb-4 p-3 rounded-xl text-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(245,158,11,0.05) 100%)',
                      border: '1px solid rgba(245,158,11,0.3)',
                    }}
                  >
                    <div className="flex items-center justify-center gap-2 text-gold-400 text-sm font-medium">
                      <Lock size={16} />
                      <span>Herramientas disponibles en planes de pago</span>
                    </div>
                  </div>
                )}
                
                {/* Grid de categorías */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
                  {SYSTEM_LAYOUTS.map((layout) => {
                    const IconComponent = layout.icon;
                    const isLocked = !isPaidPlan;
                    
                    return (
                      <div 
                        key={layout.id} 
                        className={`rounded-xl overflow-hidden ${isLocked ? 'opacity-50' : ''}`}
                        style={{
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.2) 100%)',
                          border: `1px solid ${layout.color}30`,
                        }}
                      >
                        {/* Header de categoría */}
                        <div 
                          className="px-2 md:px-3 py-1.5 md:py-2 flex items-center gap-1.5 md:gap-2"
                          style={{
                            background: `linear-gradient(135deg, ${layout.color}20 0%, transparent 100%)`,
                            borderBottom: `1px solid ${layout.color}20`
                          }}
                        >
                          <IconComponent size={14} className="md:w-4 md:h-4" style={{ color: layout.color }} weight="fill" />
                          <span className="text-[10px] md:text-xs font-bold text-white truncate">{layout.name}</span>
                          {!isLocked && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleApplyLayout(layout); }}
                              className="ml-auto text-[9px] md:text-[10px] px-1.5 md:px-2 py-0.5 rounded-full transition-all hover:scale-105"
                              style={{
                                background: `${layout.color}30`,
                                color: layout.color,
                              }}
                              title="Abrir todas"
                            >
                              Todas
                            </button>
                          )}
                        </div>
                        
                        {/* Lista de herramientas - altura ajustada para móvil */}
                        <div className="p-1.5 md:p-2 space-y-0.5 md:space-y-1 max-h-[180px] md:max-h-[280px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                          {layout.windows.map((toolId) => {
                            const toolConfig = getToolFromConfig(toolId);
                            const toolInfo = TOOL_DESCRIPTIONS[toolId] || {};
                            const ToolIcon = toolConfig?.icon || Info;
                            
                            return (
                              <button
                                key={toolId}
                                onClick={(e) => !isLocked && handleOpenSingleTool(toolId, e)}
                                disabled={isLocked}
                                className={`w-full p-1.5 md:p-2 rounded-lg transition-all text-left flex items-center gap-1.5 md:gap-2 group min-h-[40px] ${
                                  isLocked ? 'cursor-not-allowed' : 'hover:bg-white/10 active:bg-white/20'
                                }`}
                              >
                                <div 
                                  className="w-6 h-6 md:w-7 md:h-7 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                                  style={{ 
                                    background: `${toolConfig?.color || '#00dcff'}20`,
                                  }}
                                >
                                  <ToolIcon size={12} className="md:w-3.5 md:h-3.5" style={{ color: toolConfig?.color || '#00dcff' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-[10px] md:text-xs font-medium text-white block truncate">
                                    {toolInfo.name || toolConfig?.name || toolId}
                                  </span>
                                  {toolInfo.requiredPlan === 'ultimate' && (
                                    <span className="text-[8px] md:text-[9px] text-zinc-400">Ultimate</span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Vistas personalizadas del usuario */}
                {customLayouts.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gold-500/20">
                    <h3 className="text-xs font-bold text-gold-400 mb-2 flex items-center gap-2">
                      <Star size={14} weight="fill" />
                      Mis Vistas Personalizadas
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {customLayouts.map((layout) => (
                        <div
                          key={layout.id}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-gold-500/20 group"
                        >
                          <button
                            onClick={() => handleApplyLayout(layout)}
                            className="text-sm text-white hover:text-gold-400 transition-colors"
                          >
                            {layout.name}
                          </button>
                          <span className="text-xs text-gray-500">({layout.windows?.length || 0})</span>
                          <button
                            onClick={(e) => handleDeleteCustomLayout(layout.id, e)}
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all"
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ,
        document.body
      )}
    </div>
  );
};

export default LayoutPresets;
